import { ChangeDetectionStrategy, Component, effect, input, model, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpStatusEnum } from '@core/api';
import { TranslateModule } from '@ngx-translate/core';
import { AutosuggestApi } from '@shared/api';
import { EllipsisTooltipComponent } from '@shared/components/ellipsis-tooltip/ellipsis-tooltip.component';
import { UserAccount } from '@shared/interfaces';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { AutocompleteAccountsTypeEnum } from './autocomplete-accounts.helper';

@Component({
    selector: 'autocomplete-accounts[select]',
    imports: [AutoCompleteModule, EllipsisTooltipComponent, TranslateModule, FormsModule],
    templateUrl: './autocomplete-accounts.component.html',
    styleUrl: './autocomplete-accounts.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteAccountsComponent {
    autocomplete = viewChild<AutoComplete | null>('autocompleteAccounts');

    id = input.required<string>();
    showFullNameInSelection = input<boolean>(true);
    disabled = input<boolean>(false);
    type = input<AutocompleteAccountsTypeEnum>(AutocompleteAccountsTypeEnum.ACCOUNTS);

    searchValue = model<string>('');
    selectedAccount = signal<UserAccount | null>(null);
    suggestions = signal<UserAccount[]>([]);

    select = output<UserAccount | null>();
    valueChanged = output<string>();

    constructor(private autosuggestApi: AutosuggestApi) {
        effect(() => {
            if (!this.searchValue()) {
                this.selectedAccount.set(null);
            }
        });
    }

    async getUserSuggestions(): Promise<void> {
        if (this.type() === AutocompleteAccountsTypeEnum.ACCOUNTS) {
            const response = await this.autosuggestApi.suggestAccountsForImpersonation({
                searchValue: this.selectedAccount()?.email || this.searchValue(),
                take: 30,
            });

            if (response.status !== HttpStatusEnum.SUCCESS) {
                return;
            }

            this.suggestions.set(response.data || []);
            this.showSuggestionsPanel();
            return;
        }

        if (this.type() === AutocompleteAccountsTypeEnum.CENTRAL_SHIPPING) {
            const response = await this.autosuggestApi.suggestCentralShippingUsers({
                searchValue: this.selectedAccount()?.email || this.searchValue(),
                take: 30,
            });
            this.suggestions.set(response.data?.users ?? []);
            this.showSuggestionsPanel();
            return;
        }
    }

    selectAccount(user: UserAccount): void {
        const label =
            `${user.firstName || ' '} ${user.lastName || ''}${user.email ? ' (' + user.email + ')' : ''}`.trim();
        this.selectedAccount.set({ ...user, label });
        this.searchValue.set(this.showFullNameInSelection() ? label : (user.email ?? ''));
        this.select.emit(user);
        this.valueChanged.emit(user.email);
    }

    clearSelection(): void {
        this.selectedAccount.set(null);
        this.select.emit(null);
        this.getUserSuggestions();
    }

    private showSuggestionsPanel(): void {
        if (this.autocomplete()?.focused) {
            this.autocomplete()?.show();
        }
    }
}
